import { formatDate } from './format';

/**
 * Visit-date rejections from POST /sale and POST /sale/quote.
 *
 * - 400 VISIT_DATE_REQUIRED | VISIT_DATE_INVALID | VISIT_DATE_OUT_OF_RANGE
 * - 409 VISIT_DATE_BLOCKED `{ blocked: true, reason, visitDate, dateFrom, dateTo }`
 *
 * A capacity-full 409 ALSO carries `blocked: true`, so callers must branch on
 * `errorCode`, never on the status or the `blocked` flag alone.
 */
export const VISIT_DATE_BLOCKED = 'VISIT_DATE_BLOCKED';

const VISIT_DATE_400_COPY: Record<string, string> = {
  VISIT_DATE_REQUIRED: 'Tanggal kunjungan belum dipilih. Pilih tanggal kunjungan terlebih dahulu.',
  VISIT_DATE_INVALID: 'Tanggal kunjungan tidak valid. Pilih ulang tanggal kunjungan.',
  VISIT_DATE_OUT_OF_RANGE:
    'Tanggal kunjungan di luar jangkauan penjualan. Pilih tanggal yang tersedia di kalender.',
};

/** Out-of-range copy naming the last bookable day when the server sent it. */
function outOfRangeMessage(maxDate: unknown): string | null {
  if (typeof maxDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(maxDate)) return null;
  return `Tanggal kunjungan di luar jangkauan penjualan (hari ini s.d. ${formatDate(maxDate)}). Pilih ulang tanggal kunjungan.`;
}

/** Structural view of an {@link ApiError} — accepts any thrown value. */
function asErrorLike(err: unknown): { errorCode?: unknown; payload?: unknown } {
  return err && typeof err === 'object' ? (err as { errorCode?: unknown; payload?: unknown }) : {};
}

interface BlockedPayload {
  reason?: unknown;
  visitDate?: unknown;
  maxDate?: unknown;
}

export function isVisitDateBlockedError(err: unknown): boolean {
  return asErrorLike(err).errorCode === VISIT_DATE_BLOCKED;
}

/** Cashier-facing copy for a blocked visit date, with the operator's reason. */
export function blockedDateMessage(reason?: string | null, visitDate?: string | null): string {
  const when = visitDate ? `Tanggal kunjungan ${formatDate(visitDate)}` : 'Tanggal kunjungan ini';
  const why = reason?.trim() ? ` karena "${reason.trim()}"` : '';
  return `${when} ditutup${why} — penjualan tidak dapat diproses. Pilih tanggal lain.`;
}

/**
 * Indonesian message for a visit-date rejection, or null when the error is
 * not a visit-date one (callers fall back to their generic handling).
 */
export function visitDateErrorMessage(err: unknown): string | null {
  const { errorCode: code, payload } = asErrorLike(err);
  if (typeof code !== 'string' || !code) return null;
  if (code === VISIT_DATE_BLOCKED) {
    const p = (payload && typeof payload === 'object' ? payload : {}) as BlockedPayload;
    return blockedDateMessage(
      typeof p.reason === 'string' ? p.reason : null,
      typeof p.visitDate === 'string' ? p.visitDate : null,
    );
  }
  if (code === 'VISIT_DATE_OUT_OF_RANGE') {
    const p = (payload && typeof payload === 'object' ? payload : {}) as BlockedPayload;
    const named = outOfRangeMessage(p.maxDate);
    if (named) return named;
  }
  return Object.prototype.hasOwnProperty.call(VISIT_DATE_400_COPY, code)
    ? (VISIT_DATE_400_COPY[code] ?? null)
    : null;
}
